//
// # CommandPalette
//
// A VS Code-style command palette overlay. Renders as a React portal
// positioned just below the title bar, centered horizontally.
//
// Features:
//  - Auto-focused search input that filters commands by label/category
//  - Arrow key navigation with Enter to execute
//  - Parameter prompting for commands that require input
//  - Escape to close (or exit parameter prompting)
//  - Backdrop click to close
//

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Command } from "../../commandCenter";
import { formatKeybinding, type CommandCenter } from "../../commandCenter";
import { useWorkspace } from "../../context";
import { getEditorContext } from "../../editorContext";

export interface CommandPaletteProps {
  readonly commandCenter: CommandCenter;
  readonly onClose: () => void;
}

export function CommandPalette({
  commandCenter,
  onClose,
}: CommandPaletteProps) {
  const workspace = useWorkspace();
  const [searchText, setSearchText] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Parameter prompting state: when a command requires a parameter,
  // we switch to this mode instead of executing immediately.
  const [promptingCommand, setPromptingCommand] = useState<Command | null>(
    null
  );
  const [paramText, setParamText] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  // Store the previously focused element so we can restore it on close.
  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    return () => {
      // Restore focus when the palette unmounts
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  // Auto-focus the input when the palette opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Split results into runnable commands (navigable, contextually-sorted)
  // and unavailable commands (greyed-out, shown only for discoverability).
  const searchResult = promptingCommand
    ? { available: [], disabled: [] }
    : commandCenter.searchWithContext(searchText, getEditorContext(workspace));
  const filteredCommands = searchResult.available;
  const disabledCommands = searchResult.disabled;

  // Clamp selectedIndex when filtered list changes
  useEffect(() => {
    if (selectedIndex >= filteredCommands.length) {
      setSelectedIndex(Math.max(0, filteredCommands.length - 1));
    }
  }, [filteredCommands.length, selectedIndex]);

  // Scroll the selected row into view
  useEffect(() => {
    if (!listRef.current) return;
    const rows = listRef.current.children;
    if (rows[selectedIndex]) {
      rows[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const executeCommand = useCallback(
    (cmd: Command) => {
      if (cmd.parameterPrompt) {
        // Switch to parameter prompting mode
        setPromptingCommand(cmd);
        setParamText("");
        setSearchText("");
        // Re-focus input for the parameter
        setTimeout(() => inputRef.current?.focus(), 0);
      } else {
        onClose();
        void cmd.execute();
      }
    },
    [onClose]
  );

  // Keep refs to current state so the native key handler (registered once
  // with the CommandCenter) always sees the latest values.
  const stateRef = useRef({
    promptingCommand,
    paramText,
    filteredCommands,
    selectedIndex,
  });
  stateRef.current = {
    promptingCommand,
    paramText,
    filteredCommands,
    selectedIndex,
  };

  const executeCommandRef = useRef(executeCommand);
  executeCommandRef.current = executeCommand;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Register a native key handler with the CommandCenter.
  // The CommandCenter's capture-phase listener delegates to this
  // when the palette is open, since React's onKeyDown can't fire
  // after stopImmediatePropagation on window.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const {
        promptingCommand: pc,
        paramText: pt,
        filteredCommands: fc,
        selectedIndex: si,
      } = stateRef.current;

      if (e.key === "Escape") {
        if (pc) {
          setPromptingCommand(null);
          setParamText("");
        } else {
          onCloseRef.current();
        }
        return;
      }

      if (pc) {
        if (e.key === "Enter") {
          onCloseRef.current();
          void pc.execute(pt);
        }
        return;
      }

      if (e.key === "ArrowDown") {
        setSelectedIndex(fc.length === 0 ? 0 : (si + 1) % fc.length);
      } else if (e.key === "ArrowUp") {
        setSelectedIndex(
          fc.length === 0 ? 0 : (si - 1 + fc.length) % fc.length
        );
      } else if (e.key === "Enter") {
        if (fc.length > 0 && fc[si]) {
          executeCommandRef.current(fc[si]);
        }
      }
    };
    commandCenter.setPaletteKeyHandler(handler);
    return () => commandCenter.setPaletteKeyHandler(null);
  }, [commandCenter]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      // Only close if the click is on the backdrop itself
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  return createPortal(
    <div className="command-palette-backdrop" onClick={handleBackdropClick}>
      <div
        className="command-palette"
        role="dialog"
        aria-label="Command Palette"
      >
        <input
          ref={inputRef}
          className="command-palette-input"
          type="text"
          value={promptingCommand ? paramText : searchText}
          onChange={(e) => {
            if (promptingCommand) {
              setParamText(e.target.value);
            } else {
              setSearchText(e.target.value);
              setSelectedIndex(0);
            }
          }}
          placeholder={
            promptingCommand
              ? promptingCommand.parameterPrompt
              : "Type a command..."
          }
          role="combobox"
          aria-expanded={!promptingCommand}
          aria-controls="command-palette-list"
          aria-activedescendant={
            !promptingCommand && filteredCommands.length > 0
              ? `command-palette-item-${selectedIndex}`
              : undefined
          }
          autoComplete="off"
          spellCheck={false}
        />

        {promptingCommand && (
          <div className="command-palette-prompt-label">
            {promptingCommand.label}
          </div>
        )}

        {!promptingCommand && (
          <div
            ref={listRef}
            className="command-palette-list"
            id="command-palette-list"
            role="listbox"
          >
            {filteredCommands.length === 0 && disabledCommands.length === 0 && (
              <div className="command-palette-empty">No matching commands</div>
            )}
            {filteredCommands.map((cmd, idx) => (
              <div
                key={cmd.id}
                id={`command-palette-item-${idx}`}
                className={
                  "command-palette-row" +
                  (idx === selectedIndex ? " command-palette-row-selected" : "")
                }
                role="option"
                aria-selected={idx === selectedIndex}
                onClick={() => executeCommand(cmd)}
                onMouseEnter={() => setSelectedIndex(idx)}
              >
                <span className="command-palette-row-label">
                  {cmd.category && (
                    <span className="command-palette-row-category">
                      {cmd.category}:{" "}
                    </span>
                  )}
                  {cmd.label}
                </span>
                {cmd.keybinding && (
                  <span className="command-palette-keybinding">
                    {formatKeybinding(cmd.keybinding)}
                  </span>
                )}
              </div>
            ))}
            {/* Unavailable in the current context: greyed-out and
                non-selectable, shown below a divider for discoverability. */}
            {disabledCommands.length > 0 && (
              <div className="command-palette-divider">Unavailable here</div>
            )}
            {disabledCommands.map((cmd) => (
              <div
                key={cmd.id}
                className="command-palette-row command-palette-row-disabled"
                role="option"
                aria-disabled={true}
                aria-selected={false}
              >
                <span className="command-palette-row-label">
                  {cmd.category && (
                    <span className="command-palette-row-category">
                      {cmd.category}:{" "}
                    </span>
                  )}
                  {cmd.label}
                </span>
                {cmd.keybinding && (
                  <span className="command-palette-keybinding">
                    {formatKeybinding(cmd.keybinding)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
